import type { EventItem } from '@/types';

interface Black5PanelProps {
  event: EventItem;
}

export default function Black5Panel({ event }: Black5PanelProps) {
  const values = event.history.map((item) => item.value);
  const min = Math.min(...values) - 2;
  const max = Math.max(...values);

  return (
    <section className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-background-200">
        <h2 className="font-heading text-base md:text-lg font-semibold text-foreground-950 flex items-center gap-2">
          <i className="ri-line-chart-line text-accent-600 w-5 h-5 flex items-center justify-center" />
          黑5数据详情
        </h2>
        <span className="text-xs text-foreground-500">往期与当前对比</span>
      </div>

      <div className="px-4 md:px-6 py-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6">
          <div className="bg-primary-50 border border-primary-100 rounded-lg p-4">
            <p className="text-xs text-primary-700">黑5综合指数</p>
            <p className="mt-1 font-heading text-2xl md:text-3xl font-bold text-primary-800">
              {event.black5Index.toFixed(1)}
            </p>
          </div>
          <div className="bg-accent-50 border border-accent-100 rounded-lg p-4">
            <p className="text-xs text-accent-700">数据走势</p>
            <p className="mt-1 font-heading text-2xl md:text-3xl font-bold text-accent-700">
              {event.black5Trend === 'up' ? '上行' : event.black5Trend === 'down' ? '回落' : '持平'}
            </p>
          </div>
          <div className="bg-secondary-50 border border-secondary-100 rounded-lg p-4">
            <p className="text-xs text-secondary-700">赛事热度</p>
            <p className="mt-1 font-heading text-2xl md:text-3xl font-bold text-secondary-700">
              {event.heat}
            </p>
          </div>
        </div>

        <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-3">
          关键指标对比
        </h3>
        <div className="flex items-center justify-between text-xs text-foreground-500 mb-2 px-1">
          <span className="font-medium text-primary-700">{event.homeTeam}</span>
          <span>指标</span>
          <span className="font-medium text-secondary-700">{event.awayTeam}</span>
        </div>
        <div className="flex flex-col gap-4">
          {event.metrics.map((metric) => {
            const total = metric.home + metric.away || 1;
            const homePct = Math.round((metric.home / total) * 100);
            return (
              <div key={metric.label}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="font-heading font-semibold text-primary-700">
                    {metric.home}
                    {metric.unit}
                  </span>
                  <span className="text-xs text-foreground-600">{metric.label}</span>
                  <span className="font-heading font-semibold text-secondary-700">
                    {metric.away}
                    {metric.unit}
                  </span>
                </div>
                <div className="flex h-2.5 rounded-full overflow-hidden bg-background-200">
                  <div className="bg-primary-500" style={{ width: `${homePct}%` }} />
                  <div className="bg-secondary-400 flex-1" />
                </div>
              </div>
            );
          })}
        </div>

        <h3 className="mt-8 font-heading text-sm md:text-base font-semibold text-foreground-900 mb-3">
          往期黑5指数走势
        </h3>
        <div className="bg-background-100 rounded-lg p-4 md:p-5">
          <div className="flex items-end justify-between gap-2 h-40 md:h-48">
            {event.history.map((item, index) => {
              const height = Math.max(12, ((item.value - min) / (max - min || 1)) * 100);
              const isLast = index === event.history.length - 1;
              return (
                <div key={item.round} className="flex-1 flex flex-col items-center justify-end h-full gap-2">
                  <span className={`text-[11px] font-heading font-semibold ${isLast ? 'text-accent-600' : 'text-foreground-600'}`}>
                    {item.value}
                  </span>
                  <div
                    className={`w-full max-w-[38px] rounded-t-md transition-all ${
                      isLast ? 'bg-accent-500' : 'bg-primary-400'
                    }`}
                    style={{ height: `${height}%` }}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex items-start justify-between gap-2 mt-2 pt-3 border-t border-background-200">
            {event.history.map((item) => (
              <div key={`${item.round}-label`} className="flex-1 text-center">
                <p className="text-[11px] text-foreground-600 truncate">{item.round}</p>
                <p className="text-[10px] text-foreground-400 truncate">{item.result}</p>
              </div>
            ))}
          </div>
        </div>

        <h3 className="mt-8 font-heading text-sm md:text-base font-semibold text-foreground-900 mb-3">
          数据分析
        </h3>
        <p className="text-sm text-foreground-700 leading-relaxed">{event.analysis}</p>

        <div className="mt-6 flex flex-col gap-2">
          <span className="text-xs text-foreground-500">重点看点</span>
          <div className="flex flex-wrap gap-2">
            {event.hotspots.map((spot) => (
              <span
                key={spot}
                className="px-3 py-1.5 rounded-full text-xs bg-secondary-100 text-secondary-900 border border-secondary-200"
              >
                {spot}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}